import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { listDevicesPerSite } from '../../redux/actions/listDevicesPerSite';
import './MatterportViewer.css';
import Modal from "../../generic_components/Modal/Modal";
import Input from "../../generic_components/Input/Input";
import Button from "../../generic_components/Button/Button";
import Select from "../../generic_components/Select/Select";
import GenericTooltip from '../../generic_components/Tooltip/Tooltip';
import { useToast } from '../../generic_components/Toast/Toast';
import NoImageAvailable from '../servers/sitemap/noImageAvailable/NoImageAvailable';
import { ICONS_OPTIONS } from "./Icons";

export default function MatterportModelViewer({
  modelId,
  hwid = null,
  onTagUpdateRequired = null,
  url = '',
  siteName = '',
  editMode = false,
  tags
}) {
  const containerRef = useRef(null);
  const sdkRef = useRef(null);
  const isUserEditingRef = useRef(false);

  const sdkKey = '88dcwi4yga130p8q90qzmsqtd';
  const sdkScriptUrl = 'https://static.matterport.com/showcase-sdk/latest.js';

  const dispatch = useDispatch();
  const devicesPerSite = useSelector(state => state.devicesPerSite);

  const [cameras, setCameras] = useState([]);
  const [tagCameraMappings, setTagCameraMappings] = useState([]);
  const [sdkReady, setSdkReady] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [selectedImageUrl, setSelectedImageUrl] = useState(null); 
  const [selectedImageUpdateTime, setSelectedImageUpdateTime] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [liveTags, setLiveTags] = useState({});
  const [mediaUrlError, setMediaUrlError] = useState(false);

  // on edit tag state
  const [editingTag, setEditingTag] = useState(null);
  const [showDeleteTagConfirm, setShowDeleteTagConfirm] = useState(false);
  const lastHoveredTagRef = useRef(null);
  const editorOpenRef = useRef(false);
  const hoverLockRef = useRef(false);

  // image loaded state
  const [imageLoaded, setImageLoaded] = useState(false);
  const [animate, setAnimate] = useState(false);

  const toast = useToast();

  // Description input now writes directly to editingTag, no draftDescription
const handleDescriptionChange = (e) => {
  isUserEditingRef.current = true;
  setEditingTag(prev => ({ ...prev, description: e.target.value }));
};

  // Handle image load event
  const handleImageLoad = () => {
        setImageLoaded(true);
  };

  // Close the image render modal
  const closeModal = () => {
      setShowModal(false);
      setImageLoaded(false); // Reset states
      setAnimate(false);
  };

  const handleDeleteTag = () => {
    setShowDeleteTagConfirm(true);
  }

   // Extract and format date part from timestamp string
  const getDatePart = (timestamp) => {
    try {
        if (typeof timestamp !== "string" || !timestamp.includes("T")) {
        throw new Error("Invalid timestamp format");
        }

        const datePart = timestamp.split("T")[0]; // "2025-11-05"

        if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        throw new Error("Invalid date part");
        }

        // Convert to readable format: "Nov 5, 2025"
        const formattedDate = new Date(datePart).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        });

        return formattedDate; // e.g., "Nov 5, 2025"
    } catch (err) {
        console.error("getDatePart error:", err.message);
        return null;
    }
  } 

  const handleEditTagCancel = () => {
      setEditingTag(null);
      editorOpenRef.current = false;
      hoverLockRef.current = false;
      lastHoveredTagRef.current = null;  
      setMediaUrlError(false);
  }

const handleConfirmDeleteTag = async () => {
  let tagId = '';
  try {
    tagId = editingTag?.id;  

    const response = await fetch(`${url}/matterport/delete-matterport-tag`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tagId, modelId })
    });

    if (!response.ok) {
      console.error('❌ Tag deletion failed:', response.status);
      toast.error('Failed to delete tag', { autoClose: 5000 });
      return;
    }

    toast.success(
      'Tag deleted successfully. It may take upto five minutes for the changes to reflect. ',
      { autoClose: 5000 

    });

    // remove deleted tag from local sdk showcase immediately to avoid stale tag display
    if (sdkRef.current) {
      try {
        await sdkRef.current.Tag.remove(tagId);
        console.log(`Tag ${tagId} removed from SDK showcase`);
      } catch (err) {
        console.error(`Failed to remove tag ${tagId} from SDK:`, err);
      }
    }
    
    setEditingTag(null);
    editorOpenRef.current = false;
    hoverLockRef.current = false;
    lastHoveredTagRef.current = null;  
    setMediaUrlError(false);
    setShowDeleteTagConfirm(false);
    setEditingTag(null); // Close the editor modal

  } catch (error) {
    console.error("Failed to delete tag:", error);
    toast.error("An error occurred while deleting the tag", { autoClose: 5000 });
  }
};

  /**
   Validate that a string is a secure URl
   @param {string} url - The URL string to validate
    @returns {boolean} - True if the URL is valid and secure, false otherwise
   */
  const isValidSecureUrl = (url) => {
  // Don't validate while user is still typing a partial URL
  if (!url || url.trim() === "") {
    setMediaUrlError(null);
    return false;
  }

  // Suppress errors for incomplete URLs — user is still typing
  const looksComplete = url.includes(".") && !url.endsWith("://");
  if (!looksComplete) {
    setMediaUrlError(null);
    return false;
  }

  try {
    const parsedUrl = new URL(url);
    if (parsedUrl.protocol !== "https:") {
      setMediaUrlError("URL must start with https://");
      return false;
    }
    if (!parsedUrl.hostname) {
      setMediaUrlError("URL must have a valid hostname");
      return false;
    }
    setMediaUrlError(null); // Clear error on valid URL
    return true;
  } catch (err) {
    setMediaUrlError("Invalid URL format");
    return false;
  }
};
  // handle model url change in tag editor
const handleMediaUrlChange = (e) => {
  const url = e.target.value;
  setEditingTag({
    ...editingTag,
    mediaUrl: url
  });
  isValidSecureUrl(url);             
}

// handle model titel change
const handleLabelChange = (e) => {
  const newLabel = e.target.value;
  console.log("Label changed to:", newLabel);
  setEditingTag({
    ...editingTag,
    label: newLabel
  });
};

  /* ---------------- Fetch cameras ---------------- */
  useEffect(() => {
    if (hwid) dispatch(listDevicesPerSite({ hwid }));
  }, [hwid, dispatch]);

  useEffect(() => {
    if (!hwid || !devicesPerSite?.servers) { setCameras([]); return; }
    const all = devicesPerSite.servers.flatMap(s =>
      s.cameras.map(cam => ({ ...cam, server_name: s.server_name }))
    );
    setCameras([...new Map(all.map(c => [c.camera_id, c])).values()]);
  }, [devicesPerSite, hwid]);

  /* ---------------- Fetch tag ↔ camera mappings ---------------- */
  useEffect(() => {
    if (!hwid || !modelId) return;
    (async () => {
      try {
        const res = await fetch(`${url}/matterport/camera-tag-mappings/${hwid}/${modelId}`);
        if (!res.ok) return;
        const data = await res.json();
        setTagCameraMappings(data.mappings || []);
      } catch (_) {}
    })();
  }, [hwid, modelId, url]);

  /* ---------------- Camera → tag appearance ---------------- */
  const getTagProps = (status) => {
    // color: { r, g, b } normalized 0-1 floats required by SDK edit methods
    // hex: string used by the backend REST API
    if (status === 1) return { color: { r: 0.196, g: 0.804, b: 0.196 }, hex: '#32CD32', description: 'Online' };
    if (status === 0) return { color: { r: 1.0,   g: 0.388, b: 0.278 }, hex: '#FF6347', description: 'Offline' };
    return              { color: { r: 0.502, g: 0.502, b: 0.502 }, hex: '#808080', description: 'Unknown' };
  };

  // Handle click tag save (both SDK update and backend persist)
  const handleEditTagSave = async () => {
  if (!editingTag || !sdkRef.current || mediaUrlError) return;

  try {
    // persist to backend (keeps your existing architecture)
    const res = await fetch(`${url}/matterport/update-matterport-tag  `, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        tagId: editingTag.id,
        label: editingTag.label,
        description: editingTag.description,
        mediaUrl: editingTag.mediaUrl,
        icon: editingTag.icon,
        color: editingTag.color,
        modelId,
        hwid
      })
    });

   if (!res.ok) {
      console.error("Tag update failed:", res.status);
      toast.error("Failed to save tag changes", { autoClose: 5000 });
      return;
    }

    // Update local liveTags cache to avoid stale UI
    setLiveTags(prev => ({
      ...prev,
      [editingTag.id]: {
        ...prev[editingTag.id],
        label: editingTag.label,
        description: editingTag.description,
        mediaUrl: editingTag.mediaUrl,
        icon: editingTag.icon,
        color: editingTag.color
      }
    }));

    setEditingTag(null); 
    const successMsg = "Tag changes saved successfully. It may take upto five minutes for the changes to reflect.";
    toast.success(successMsg, { autoClose: 5000 });

  } catch (err) {
    console.error("❌ Hover tag edit failed:", err);
    toast.error("An error occurred while saving tag changes", { autoClose: 5000 });
  }
};

  /* ---------------- Initialize SDK ---------------- */
  useEffect(() => {
    if (!modelId || !sdkKey || !containerRef.current) {
      if (!modelId) console.warn('Model ID is required to load Matterport showcase');
      if (!sdkKey) console.warn('SDK key is required to load Matterport showcase');
      if (!containerRef.current) console.warn('Container ref is not set for Matterport showcase');
      return;
    };

    const containerNode = containerRef.current;
    let mounted = true;

    const init = async () => {
      try {
        setLoadingStatus('Initializing...');
        containerNode.innerHTML = '';
        setSdkReady(false);

        if (!window.MP_SDK) {
          console.log("loading SDK script ...")
          setLoadingStatus('Loading SDK script...');
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = sdkScriptUrl;
            script.async = true;
            script.onload = resolve;
            script.onerror = () => reject(new Error('Failed to load SDK script'));
            document.head.appendChild(script);
            setTimeout(() => reject(new Error('SDK script timeout')), 15000);
          });
        }

        if (!mounted) return;

        setLoadingStatus('Creating showcase...');
        const iframe = document.createElement('iframe');
        iframe.setAttribute('width', '100%');
        iframe.setAttribute('height', '100%');
        iframe.setAttribute('frameborder', '0');
        iframe.setAttribute('allowfullscreen', 'true');
        iframe.setAttribute('allow', 'xr-spatial-tracking');
        iframe.setAttribute('referrerpolicy', 'no-referrer');
        iframe.style.border = 'none';
        iframe.src = `https://my.matterport.com/show/?m=${modelId}&play=1&applicationKey=${sdkKey}&referrer=https://ronin.artoflogic.ai`;
        containerNode.appendChild(iframe);

        setLoadingStatus('Loading 3D Model...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        if (!mounted) return;

        const sdk = await window.MP_SDK.connect(iframe, sdkKey, '');
       
        await Promise.race([
          sdk.App.state.waitUntil(state => state.phase === sdk.App.Phase.PLAYING),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Showcase timeout')), 60000))
        ]);

        if (!mounted) return;

        sdkRef.current = sdk;
        setSdkReady(true);
        setLoadingStatus('Ready');
      } catch (err) {
        console.error('❌ SDK init failed:', err);
        setLoadingStatus(`Error: ${err.message}`);
        setSdkReady(false);
      }
    };

    init();

    return () => {
      mounted = false;
      sdkRef.current = null;
      setSdkReady(false);
      setLoadingStatus('');
      if (containerNode) containerNode.innerHTML = '';
    };
  }, [modelId, sdkKey, sdkScriptUrl]);

// pad and edit tags
  const patchAndUpdateSDK = useCallback(async (updates) => {
    const sdk = sdkRef.current;

    await Promise.all([

      // ── 1. Persist to backend ──────────────────────────────────────
      (async () => {
        try {
          const payload = updates.map(u => ({
            tag_id: u.tag_id,
            color: u.hex,
            description: u.description,
            camera_id: u.camera_id,
            camera_name: u.camera_name,
            camera_status: u.camera_status,
            tag_label: u.tag_label
          }));

          const res = await fetch(`${url}/matterport/update-matterport-tags`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ hwid, modelId, tagsToUpdate: payload })
          });

          if (!res.ok) { console.error('❌ Backend patch failed:', res.status); return; }

          const result = await res.json();
          console.log('✅ Backend patch persisted:', result);
          onTagUpdateRequired?.(result);
        } catch (err) {
          console.error('❌ Backend patch error:', err);
        }
      })(),

      // ── 2. Update live SDK session immediately ─────────────────────
      (async () => {
        if (!sdk) { console.warn('⚠️ SDK not ready for visual update'); return; }

        for (const update of updates) {
          try {
            console.log(`🎨 SDK updating "${update.tag_label}" → ${update.description}`);

            // Updates tag disc color instantly in current session
            await sdk.Tag.editColor(update.tag_id, update.color);

            // Updates tag billboard text instantly in current session
            await sdk.Tag.editBillboard(update.tag_id, {
              label: update.tag_label,
              description: update.description
            });

            console.log(`  ✅ "${update.tag_label}" updated in SDK`);
          } catch (err) {
            console.error(`  ❌ SDK edit failed for ${update.tag_id}:`, err.message);
          }
        }
      })()

    ]);
  }, [hwid, modelId, url, onTagUpdateRequired]);

  // Helper to convert hex color string to normalized RGB object for SDK methods
  const hexToRgbNormalized = (hex) => {
  const bigint = parseInt(hex.replace('#',''), 16);
  return {
    r: ((bigint >> 16) & 255) / 255,
    g: ((bigint >> 8) & 255) / 255,
    b: (bigint & 255) / 255
  };
};

/* Model name with site name sync */
  useEffect(() => {
    if (!sdkReady || !sdkRef.current) return;

    const syncModelName = async () => {
      const sdk = sdkRef.current;     
      const modelDetails = await sdk.Model.getDetails(() => true);
      const modelName = modelDetails.name || '';

      const modelNameNeedUpdate = modelName && modelName !== siteName; // ensure mode name is set as site name
      if (modelNameNeedUpdate) {
        try {
          await fetch(`${url}/matterport/update-model-name`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ modelId, siteName })
          });         
        }
        catch (err) {
          console.error('❌ Model name update failed:', err);
        }
    }
  }
    syncModelName();
  }, [sdkReady, modelId, siteName, url]);

  /* ---------------- Tag sync --------------------------------------- */
  useEffect(() => {
    if (!sdkReady) return;

    const syncTags = async () => {
      try {       
        const res = await fetch(`${url}/matterport/tags/${modelId}`);
        if (!res.ok) return;

        const { tags = [] } = await res.json();
        const updates = [];

        for (const tag of tags) {
          const mapping = tagCameraMappings.find(m => m.tag_id === tag.id);
          if (!mapping) continue;

          const camera = cameras.find(c => c.camera_id === mapping.camera_id);
          if (!camera) continue;

          const expected = getTagProps(camera.camera_status);
          
          const sdk = sdkRef.current;
          const modelDetails = await sdk.Model.getDetails(() => true);
          const modelName = modelDetails.name || '';

          const modelNameNeedUpdate = modelName && modelName !== siteName; // ensure mode name is set as site name

          if (tag.description !== expected.description || tag.label !== camera.camera_name || modelNameNeedUpdate) {
            updates.push({
              tag_id:      tag.id,
              tag_label:   camera.camera_name,
              color:       expected.color,  // { r, g, b } for SDK
              hex:         expected.hex,    // hex string for backend
              description: expected.description,
              camera_id:   camera.camera_id,
              camera_name: camera.camera_name,
              camera_status: camera.camera_status
            });
          }
        }
        if (updates.length === 0) {          
          return;
        }
        console.log(`${updates.length} tag(s) need updating`);
        await patchAndUpdateSDK(updates);

      } catch (err) {
        console.error('❌ Tag sync failed:', err);
      }
    };

    syncTags();
  }, [sdkReady, cameras, tagCameraMappings, modelId, url, patchAndUpdateSDK, siteName]);

 // RECONCILE
useEffect(() => {
  if (!sdkReady || !sdkRef.current) return;

  const reconcile = async () => {
    try {
      const sdk = sdkRef.current;

      // Ensure tag data is fully loaded
      const showcaseTags = await sdk.Tag.data.waitUntil(
        data => data && Object.keys(data).length > 0
      );

      setLiveTags(showcaseTags);

      const res = await fetch(`${url}/matterport/tags/${modelId}`);
      if (!res.ok) return;

      const { tags: backendTags = [] } = await res.json();

      for (const backendTag of backendTags) {
        const liveTag = showcaseTags[backendTag.id];       
        if (!liveTag) continue;

        const needsFix =
          liveTag.description !== backendTag.description ||
          liveTag.label !== backendTag.label ||
          liveTag.color !== hexToRgbNormalized(backendTag.color) ||
          liveTag.iconId !== backendTag.icon ||
          liveTag.mediaUrl !== backendTag.media;

        if (needsFix) {
          await sdk.Tag.editColor(
            backendTag.id,
            hexToRgbNormalized(backendTag.color)
          );

          await sdk.Tag.editBillboard(backendTag.id, {
            label: backendTag.label,
            description: backendTag.description,
            media: backendTag.media
          });

          await sdk.Tag.editIcon(
            backendTag.id,
            backendTag.icon
          );
        }
      }
    } catch (err) {
      console.error("❌ Reconciliation failed:", err);
    }
  };
  reconcile();
}, [sdkReady, modelId, url]);

// live update sdk showcase tag data while editing tag
useEffect(() => {
  if (!sdkReady || !sdkRef.current || !editingTag) return;

    const sdk = sdkRef.current;
    const timeout = setTimeout(async () => {
      try {
       await sdk.Tag.editColor(
          editingTag.id,
          hexToRgbNormalized(editingTag.color)
        );
        await sdk.Tag.editBillboard(editingTag.id, {
          label: editingTag.label,
          description: editingTag.description
        });
        await sdk.Tag.editIcon(
          editingTag.id,
          editingTag.icon
        );       
      } catch (err) {
        console.error("❌ Live tag update failed:", err);
      }
    }, 600); // debounce 600ms

    return () => clearTimeout(timeout);

}, [editingTag, sdkReady]);

// onlick tag to fetch camera snapshot and show in modal
// On click tag: fetch camera snapshot OR open tag editor
useEffect(() => {
  if (!sdkReady || !sdkRef.current) return;
  const sdk = sdkRef.current;

  const handleClick = async (tagSid) => {
    console.log("Clicked Mattertag SID:", tagSid);

    const tagData = tags.find(t => t.id === tagSid);

    if (!tagData) {
      console.warn(`No tag data found for SID ${tagSid}`);
      return;
    };

    if (editMode) {
      // ----- EDIT MODE: open tag editor -----
      setEditingTag({
        id: tagSid,
        label: tagData.label || "",
        description: tagData.description || "",
        mediaUrl: tagData.media || "",
        icon: tagData.icon || "",
        color: tagData.color || "#03687d"
      });
      editorOpenRef.current = true;
      return; // don't fetch snapshot in edit mode
    }

    // ----- VIEW MODE: fetch camera snapshot -----
    try {
      const res = await fetch(`${url}/matterport/camera-snapshot/${tagSid}`);
      if (!res.ok) {
        console.error('❌ Snapshot fetch failed:', res.status);
        return;
      }

      const data = await res.json();

      if (data?.snapShotData?.snapshot_url) {
        console.log(`Snapshot data tag ${tagSid}:`, data.snapShotData);

        setSelectedImageUrl(data.snapShotData.snapshot_url);
        setSelectedImageUpdateTime(data.snapShotData.monthly_image_update_time);
        setShowModal(true);
        setAnimate(true);
      } else {
        console.warn(`No snapshot URL found for tag ${tagSid}`);
      }
    } catch (err) {
      console.error(`Failed to fetch snapshot for tag ${tagSid}:`, err);
      setSelectedImageUrl(null);
      setSelectedImageUpdateTime(null);
      setShowModal(false);
    }
  };

  sdk.on(sdk.Mattertag.Event.CLICK, handleClick);

  return () => {
    sdk.off(sdk.Mattertag.Event.CLICK, handleClick);
  };
}, [sdkReady, url, editMode, liveTags, tags]);

  /* ---------------- Render ---------------------------------------- */
  return (
    <div
      className="matterport-viewer-wrapper"   
    >
      {loadingStatus && !sdkReady && (
          <div
            className={`matterport-viewer-loading-overlay ${loadingStatus.startsWith('Error:') ? 'error' : ''}`}
          >
            <div className="status-icon">
              {loadingStatus.startsWith('Error:') ? '❌' : '⏳'}
            </div>
            <div className="status-text">
              {loadingStatus}
            </div>
          </div>
        )}
      <div
        className="matterport-container"
        ref={containerRef}       
      />
       {/* Tag Edit Modal */}
        {editingTag && (
          <Modal
            show={true}
            onHide={() => {
              setEditingTag(null);
              editorOpenRef.current = false;
              hoverLockRef.current = false;     
              lastHoveredTagRef.current = null;
            }}
            position="top-end"
            size="sm"
          >
            <Modal.Header>
              <Modal.Title>
                Edit Tag
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className='edit-tag-fields'>
                <div>
                  <label className='edit-tag-label'>Title</label>
                  <Input
                    type="text"
                    value={editingTag.label}
                    onChange={handleLabelChange}                 
                  />
                </div>
                <div>
                  <label className='edit-tag-label'>Description</label>
                  <Input
                  value={editingTag.description}
                  onChange={handleDescriptionChange}
                  rows={3}                                   
                  />
                </div>
                <div>
                  <label className='edit-tag-label'>Media URL</label>
                  <Input
                    value={editingTag.mediaUrl}
                    onChange={handleMediaUrlChange}
                    placeholder="https://www.youtube.com/watch?v=dAdsl9x_Dyw"                  
                  />
                  {mediaUrlError && <div className="error-message">{mediaUrlError}</div>}
                </div>
                <div>
                  <label className='edit-tag-label'>Icon</label>
                  <Select
                  value={editingTag.icon}
                  onChange={(e) => 
                    setEditingTag({
                      ...editingTag,
                      icon: e.target.value
                    })
                  }
                  options={ICONS_OPTIONS}
                  placeholder="Select an icon"
                  /> 
                </div>
                <div>
                  <label className='edit-tag-label'>Color</label>
                  <Input
                  type="color"
                  value={editingTag.color}
                  onChange={(e) => 
                    setEditingTag({
                      ...editingTag,
                      color: e.target.value
                    })
                  }
                  />
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
             <div style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <GenericTooltip content="Delete this tag" placement="right">
                    <Button variant='danger' onClick={handleDeleteTag}>
                      Delete
                    </Button>
                  </GenericTooltip>
                </div>
                <div>
                  <Button
                    variant='ghost'
                    onClick={ handleEditTagCancel}
                    >
                    Cancel
                  </Button>
                  <Button variant='gradient' onClick={handleEditTagSave}>
                    Save
                  </Button>
                </div>
              </div>
             </div>
            </Modal.Footer>
          </Modal>
          )}

       {/* Modal to show camera image */}
      {
        selectedImageUrl && selectedImageUpdateTime && (
          <Modal
            show={showModal}
            onHide={closeModal}
            position="top-end"
            size="md"
            >
            <Modal.Header>
                <Modal.Title>               
                    { 
                        getDatePart(selectedImageUpdateTime) && (
                            <div className="image-update-time">
                                <span>Last update: </span>
                                {getDatePart(selectedImageUpdateTime)}
                                <span> | Monthly shanpshot</span>
                            </div>
                        )
                    }

                </Modal.Title>
            </Modal.Header>
            <Modal.Body
                // className="modal-color"
                style={{
                    height: imageLoaded ? "auto" : "300px", // Reserve space
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {selectedImageUrl ? (
                    <div
                        style={{
                            position: "relative",
                            overflow: "hidden",
                            borderRadius: "8px",
                        }}
                    >
                        <img
                            src={selectedImageUrl}
                            alt="Camera Preview"
                            onLoad={handleImageLoad}
                                style={{
                                width: "100%",
                                height: "100%", // 👈 Stretch to container height
                                objectFit: "cover", // 👈 Keep image ratio nicely
                                filter: animate ? "blur(0px)" : "blur(20px)",
                                opacity: animate ? 1 : 0.7,
                                transition: "filter 0.8s ease, opacity 0.8s ease",
                            }}
                        />
                        
                    </div>
                ) : (
                    <NoImageAvailable />
                )}
            </Modal.Body>                                
          </Modal>          
        )
      }
      {/* Modeal to delete tag confirmation */}
      <Modal show={showDeleteTagConfirm} onHide={() => setShowDeleteTagConfirm(false)} size="sm" position="top-right">
        <Modal.Header>
          <Modal.Title>
            Confirm Delete Tag
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this tag? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="ghost" onClick={() => setShowDeleteTagConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDeleteTag}>
            Delete Tag
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
}